<template>
  <core-modal-marketing
    :tag="content.modalEyebrow"
    tag-icon="i-ri-vip-crown-2-line"
    :title="content.modalTitle"
    :subtitle="content.modalDescription"
    header-icon="i-ri-vip-crown-2-line"
  >
    <div class="mx-auto max-w-6xl">
      <ClientOnly>
        <template v-if="pending">
          <div class="grid gap-4 lg:grid-cols-3">
            <core-upsell-product-card-skeleton
              v-for="index in 3"
              :key="index"
            />
          </div>
          <div class="mt-4 h-48 animate-pulse rounded-2xl bg-slate-100 ring-1 ring-slate-200" />
        </template>

        <core-contextual-error-box
          v-else-if="fetchError"
          size="sm"
          class="my-3 rounded-md border-2 border-gray-200"
          :action="retry"
        >
          <template #heading>Unable to Load Club Plans</template>
          <template #default>Please try again or contact support.</template>
        </core-contextual-error-box>

        <template v-else>
          <section>
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Choose a membership path
            </p>

            <div class="mt-2 grid gap-4 lg:grid-cols-3">
              <article
                v-for="plan in standardPlans"
                :key="plan.id"
                class="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div class="flex flex-1 flex-col items-center text-center">
                  <img
                    :src="plan.image"
                    :alt="plan.imageAlt"
                    class="h-32 w-auto object-contain"
                  >

                  <div class="min-w-0">
                    <h2 class="text-lg font-semibold text-slate-900">
                      {{ plan.name }}
                    </h2>
                    <p class="mt-1.5 text-sm leading-6 text-slate-600">
                      {{ plan.small_description }}
                    </p>
                  </div>
                </div>

                <div class="mt-6 text-center">
                  <div>
                    <core-price-display
                      class="text-3xl"
                      :base-price="plan.price_cents"
                      :discounted-price="plan.promotionPrice"
                      :precision="0"
                      :precision-slashed="0"
                      :show-suffix="false"
                    />
                    <span class="text-base font-medium text-slate-500">
                      USD/per month
                    </span>
                  </div>
                </div>

                <div class="mt-auto pt-6">
                  <u-button
                    color="primary"
                    size="lg"
                    block
                    class="justify-center"
                    label="Add to Cart"
                    :loading="addingProductId === plan.id"
                    :disabled="Boolean(addingProductId)"
                    @click="addToCart(plan)"
                  />
                </div>
              </article>
            </div>
          </section>

          <section class="mt-4 rounded-2xl border border-slate-300 bg-gradient-to-br from-white to-slate-100 p-5 shadow-sm ring-1 ring-slate-200">
            <div class="flex items-center gap-5 sm:gap-6">
              <img
                :src="platinumPlan.image"
                :alt="platinumPlan.imageAlt"
                class="max-h-36 w-auto max-w-28 shrink-0 object-contain sm:max-w-40"
              >

              <div class="min-w-0 flex-1">
                <h2 class="text-xl font-semibold text-slate-900">
                  {{ platinumPlan.name }}
                </h2>
                <p class="mt-1.5 text-sm leading-6 text-slate-600">
                  {{ platinumPlan.small_description }}
                </p>

                <div class="mt-5 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
                  <div>
                    <core-price-display
                      class="text-3xl"
                      :base-price="platinumPlan.price_cents"
                      :discounted-price="platinumPlan.promotionPrice"
                      :precision="0"
                      :precision-slashed="0"
                      :show-suffix="false"
                    />
                    <span class="text-base font-medium text-slate-500">
                      USD/per month
                    </span>
                  </div>

                  <u-button
                    color="primary"
                    size="lg"
                    class="justify-center"
                    label="Add to Cart"
                    :loading="addingProductId === platinumPlan.id"
                    :disabled="Boolean(addingProductId)"
                    @click="addToCart(platinumPlan)"
                  />
                </div>
              </div>
            </div>
          </section>

          <core-upsell-features
            class="mt-8 text-left"
            :items="content.included"
          />

          <p class="mt-8 text-center">
            <u-link
              :to="content.ctaTo"
              external
              class="text-sm text-gray-500 underline"
            >
              Learn More About FOREVER Club
            </u-link>
          </p>
        </template>
      </ClientOnly>
    </div>
  </core-modal-marketing>
</template>

<script setup>
  import clubBronzeBadge from '@/assets/images/club/forever_club_bronze-logo.png';
  import clubSilverBadge from '@/assets/images/club/forever_club_silver-logo.png';
  import clubGoldBadge from '@/assets/images/club/forever_club_gold-logo.png';
  import clubPlatinumBadge from '@/assets/images/club/forever_club_platinum-logo.png';
  import {EXTERNAL_URLS} from '~/constants/external-urls.js';

  const props = defineProps({
    upsellTracking: Object
  });

  const CLUB_PRODUCT_GROUP = 'forever-club-membership-products';
  const PLAN_DEFINITIONS = [
    {
      slug: 'forever-club-25',
      fallbackImage: clubBronzeBadge,
      fallbackAlt: 'FOREVER Club Bronze'
    },
    {
      slug: 'forever-club-50',
      fallbackImage: clubSilverBadge,
      fallbackAlt: 'FOREVER Club Silver'
    },
    {
      slug: 'forever-club-100',
      fallbackImage: clubGoldBadge,
      fallbackAlt: 'FOREVER Club Gold'
    },
    {
      slug: 'forever-club-250',
      fallbackImage: clubPlatinumBadge,
      fallbackAlt: 'FOREVER Club Platinum'
    }
  ];

  const content = {
    modalEyebrow: 'FOREVER Club',
    modalTitle: 'Save more while you preserve more.',
    modalDescription: 'FOREVER Club is a monthly savings plan that helps you protect more memories while unlocking ongoing savings across the FOREVER experience.',
    ctaTo: EXTERNAL_URLS.foreverClub,
    included: [
      {
        title: 'Extra 5% off',
        description: 'Use CLUB5 on almost everything you buy across FOREVER.',
        icon: 'i-ri-percent-line'
      },
      {
        title: 'Free storage rewards',
        description: 'Earn FOREVER Storage credits over time just by staying active.',
        icon: 'i-ri-hard-drive-3-line'
      },
      {
        title: 'Member-only offers',
        description: 'Unlock occasional promotions and perks reserved for Club members.',
        icon: 'i-ri-vip-crown-2-line'
      }
    ]
  };

  const {
    products,
    pending,
    error,
    retry
  } = usePricingGroup({
    groupSlug: CLUB_PRODUCT_GROUP,
    includeDescriptions: true,
    server: false
  });

  const mappedPlans = computed(() => PLAN_DEFINITIONS.map(definition => {
    const product = products.value?.find(item => item.slug === definition.slug);

    if (!product) {
      return null;
    }

    const imageUrl = product.product_images?.[0]?.url;

    return {
      ...product,
      image: imageUrl
        ? imageUrl.replace('thumb', 'medium')
        : definition.fallbackImage,
      imageAlt: imageUrl && product.name
        ? `${product.name} Badge`
        : definition.fallbackAlt
    };
  }));

  const hasRequiredProducts = computed(() => mappedPlans.value.every(Boolean));
  const fetchError = computed(() =>
    !pending.value &&
    (
      Boolean(error.value) ||
      !hasRequiredProducts.value
    )
  );
  const standardPlans = computed(() =>
    hasRequiredProducts.value ? mappedPlans.value.slice(0, 3) : []
  );
  const platinumPlan = computed(() =>
    hasRequiredProducts.value ? mappedPlans.value[3] : null
  );

  const addingProductId = ref(null);

  async function addToCart(product) {
    if (addingProductId.value) {
      return;
    }

    addingProductId.value = product.id;

    try {
      trackUpsellCta({
        tracking: props.upsellTracking,
        product
      });

      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });

      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      addingProductId.value = null;
    }
  }
</script>
